import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { TitleWithHTMLBlock, DetailsModule as Value } from "@reactivated";

type IProps = Immutable<{
    value: Value;
}>;

type DetailItem = Immutable<TitleWithHTMLBlock>;

// Render a single column. The first column carries the legacy modifier
// classes used by the two-column layout; columns 2/3 do not. The "colums"
// spelling in `pdp-details__item--2-colums` is a legacy typo that the CSS
// targets verbatim -- do not "fix" it.
const renderColumn = (
    title: string | null | undefined,
    items: Immutable<DetailItem[]> | null | undefined,
    isFirst: boolean,
): React.JSX.Element => {
    const itemClass = isFirst
        ? "pdp-details__item pdp-details__item--2-colums"
        : "pdp-details__item";
    const titleClass = isFirst
        ? "pdp-details__item__title pdp-details__item__title--full-width"
        : "pdp-details__item__title";
    const copyClass = isFirst
        ? "pdp-details__item__copy pdp-details__item__copy--full-width pdp-section__desc"
        : "pdp-details__item__copy pdp-section__desc";
    return (
        <div className="pdp-details__column">
            <h3 className="pdp-details__sub-title">{title}</h3>
            <div className="pdp-details__body">
                {(items ?? []).map((detail, i) => (
                    <div key={i} className={itemClass}>
                        <div className={titleClass}>{detail.title}</div>
                        {/* `copy` is a server-rendered rich-text string that
                            already includes the `<div class="rich-text">`
                            wrapper (matching the legacy `{{ detail.copy|safe }}`
                            output). Pass it through verbatim; do not add an
                            extra wrapper element. */}
                        <div
                            className={copyClass}
                            dangerouslySetInnerHTML={{
                                __html: detail.copy ?? "",
                            }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export const DetailsModule = (props: IProps): React.JSX.Element => {
    const v = props.value;
    return (
        // The `id="pdp-features"` is load-bearing: configurator.tsx anchors the
        // upsell tooltip to this element. Keep it verbatim.
        <div id="pdp-features" className="pdp-details l-full-width">
            <div className="pdp-details__container pdp-section l-capped-width">
                <h2 className="pdp-details__title pdp-section__title">
                    {t`Product details`}
                </h2>
                <div className="pdp-details__columns-container">
                    {renderColumn(v.column1_title, v.column1, true)}
                    {renderColumn(v.column2_title, v.column2, false)}
                    {renderColumn(v.column3_title, v.column3, false)}
                </div>
            </div>
        </div>
    );
};
